import { lazy, Suspense, type ComponentType } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { Capacitor } from "@capacitor/core";
import Index from "./pages/Index.tsx";
import Landing from "./pages/Landing.tsx";
import Why from "./pages/Why.tsx";
import SpotifyCallback from "./pages/SpotifyCallback.tsx";

// Native app (and any in-app WebView) skips the marketing page entirely and
// launches straight into navigation. Only web visitors at "/" get the
// landing page; the web app itself lives at "/app".
const isNativeApp = (() => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
})();
import { useDeepLinks } from "./hooks/useDeepLinks";
import { OfflineShellNotice } from "./components/OfflineShellNotice";
import { attachAutoFlush } from "@/utils/pendingContributions";
import { authHashRedirect } from "@/utils/authHashRedirect";

attachAutoFlush();

// After a redeploy, previously-loaded HTML can hold references to chunk
// filenames (hashed) that no longer exist on the server. Dynamic import then
// throws "Importing a module script failed" and React shows a blank screen.
// Wrap each lazy import: on failure, force one full reload so the user gets
// the fresh index.html (and current chunk hashes). Guard with sessionStorage
// so we never enter an infinite reload loop if the failure is real.
// `any` mirrors React.lazy's own constraint (`ComponentType<any>`); `unknown`
// rejects every real component under strictFunctionTypes.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazyWithReload = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  key: string,
) =>
  lazy(() =>
    factory()
      .then((mod) => {
        // A dynamic import that "succeeds" with no module (Vite resolves
        // with undefined when a preload error is cancelled; Safari has done
        // the same after a failed chunk fetch) must be treated as a failed
        // load, or React.lazy throws reading `.default` of undefined.
        if (!mod || !mod.default) {
          throw new Error(`Failed to fetch dynamically imported module: ${key} (empty module)`);
        }
        return mod;
      })
      .catch((err) => {
      const flag = `chunk-reload:${key}`;
      if (typeof window !== "undefined" && !sessionStorage.getItem(flag)) {
        sessionStorage.setItem(flag, "1");
        window.location.reload();
        // Return a never-resolving promise so React doesn't render an error
        // before the reload kicks in.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }),
  );

// Secondary routes are lazy-loaded so the initial mobile bundle stays small.
const Contact = lazyWithReload(() => import("./pages/Contact.tsx"), "Contact");
const About = lazyWithReload(() => import("./pages/About.tsx"), "About");
const Auth = lazyWithReload(() => import("./pages/Auth.tsx"), "Auth");
// Signup-confirmation landing (emailRedirectTo target) — must also be listed
// in Supabase → Authentication → URL Configuration → Redirect URLs.
const EmailConfirmed = lazyWithReload(() => import("./pages/EmailConfirmed.tsx"), "EmailConfirmed");
const ResetPassword = lazyWithReload(() => import("./pages/ResetPassword.tsx"), "ResetPassword");
// Where the confirm-signup and reset-password emails point (PLE-28): spends
// the token, then hands off to /confirmed or /reset. See EmailLink.tsx.
const EmailLink = lazyWithReload(() => import("./pages/EmailLink.tsx"), "EmailLink");
const Admin = lazyWithReload(() => import("./pages/Admin.tsx"), "Admin");
const Workspace = lazyWithReload(() => import("./pages/Workspace.tsx"), "Workspace");
const NotFound = lazyWithReload(() => import("./pages/NotFound.tsx"), "NotFound");
const Explore = lazyWithReload(() => import("./pages/Explore.tsx"), "Explore");

const MyRoutes = lazyWithReload(() => import("./pages/MyRoutes.tsx"), "MyRoutes");
const MyContributions = lazyWithReload(() => import("./pages/MyContributions.tsx"), "MyContributions");
const BuiltByYou = lazyWithReload(() => import("./pages/BuiltByYou.tsx"), "BuiltByYou");

const Install = lazyWithReload(() => import("./pages/Install.tsx"), "Install");
const BetaInstall = lazyWithReload(() => import("./pages/BetaInstall.tsx"), "BetaInstall");
const Founder = lazyWithReload(() => import("./pages/Founder.tsx"), "Founder");
const Overlooked = lazyWithReload(() => import("./pages/Overlooked.tsx"), "Overlooked");
const RoutePage = lazyWithReload(() => import("./pages/RoutePage.tsx"), "RoutePage");
const ScenicRoutesHub = lazyWithReload(() => import("./pages/ScenicRoutesHub.tsx"), "ScenicRoutesHub");
import { SCENIC_HUBS } from "@/content/scenicRoutes";
// Required for App Store submission: privacy policy + support URL.
const Privacy = lazyWithReload(() => import("./pages/Privacy.tsx"), "Privacy");
const Support = lazyWithReload(() => import("./pages/Support.tsx"), "Support");

// Sensible defaults: don't refetch on window focus (the map and route data
// don't go stale just because the user tabbed away), and keep results for a
// minute so back-navigation is instant rather than spinner-then-data.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 60_000,
    },
  },
});

/** Inside-router child so it can call useNavigate for incoming deep links. */
const RouterShell = () => {
  useDeepLinks();
  // An auth link that landed on the bare homepage gets forwarded to the page
  // that can actually finish it — see authHashRedirect.ts for which links end
  // up here and why. The decision lives there, pure and unit-tested; this is
  // just the navigation.
  if (typeof window !== "undefined") {
    const target = authHashRedirect(window.location.pathname, window.location.hash);
    if (target) window.location.replace(target);
  }
  return (
    <>
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><div className="w-6 h-6 rounded-full border-2 border-muted border-t-foreground animate-spin" /></div>}>
      <Routes>
        <Route path="/" element={isNativeApp ? <Index /> : <Landing />} />
        <Route path="/app" element={<Index />} />
        <Route path="/why" element={<Why />} />
        <Route path="/overlooked" element={<Overlooked />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/confirm" element={<EmailLink />} />
        <Route path="/confirmed" element={<EmailConfirmed />} />
        {/* Where the password-reset email lands. Recovery tokens arrive in
            the URL hash; see ResetPassword.tsx for why this stays on the web
            rather than deep-linking into the app. */}
        <Route path="/reset" element={<ResetPassword />} />
        {/* Morgan's dashboard. Access is decided server-side by admin-stats
            against ADMIN_EMAILS — the route itself is deliberately not a
            secret, because a hidden URL is not access control. */}
        <Route path="/admin" element={<Admin />} />
        <Route path="/workspace" element={<Workspace />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about" element={<About />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/spotify/callback" element={<SpotifyCallback />} />
        <Route path="/my-routes" element={<MyRoutes />} />
        <Route path="/my-contributions" element={<MyContributions />} />
        
        <Route path="/built-by-you" element={<BuiltByYou />} />
        <Route path="/install" element={<Install />} />
        <Route path="/beta" element={<BetaInstall />} />
        <Route path="/founder" element={<Founder />} />
        <Route path="/scenic-routes" element={<ScenicRoutesHub />} />
        {SCENIC_HUBS.map((h) => (
          <Route key={h.path} path={h.path} element={<ScenicRoutesHub />} />
        ))}
        <Route path="/routes/:slug" element={<RoutePage />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/support" element={<Support />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    <FeedbackWidget />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <OfflineShellNotice />
      <BrowserRouter>
        <RouterShell />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
